import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  BookOpen,
  ClipboardList,
  BarChart3,
  FileBarChart,
  HardHat,
  Truck,
  Users,
  X,
  Calculator,
} from 'lucide-react';
import { useAuthStore, type User } from '../../stores/auth-store';
import { useUiStore } from '../../stores/ui-store';
import logoIcon from '../../assets/logo-octopaint-icon.jpeg';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: User['role'][];
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/',
    icon: <LayoutDashboard size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo', 'residente', 'compras'],
  },
  {
    label: 'Proyectos',
    path: '/proyectos',
    icon: <FolderKanban size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo', 'compras'],
  },
  {
    label: 'Mis Proyectos',
    path: '/proyectos',
    icon: <FolderKanban size={18} />,
    roles: ['residente'],
  },
  {
    label: 'Clientes',
    path: '/clientes',
    icon: <Users size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo'],
  },
  {
    label: 'Proformas',
    path: '/proformas',
    icon: <FileText size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo'],
  },
  {
    label: 'APUs',
    path: '/recetas',
    icon: <BookOpen size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo', 'residente'],
  },
  {
    label: 'Planillas',
    path: '/planillas',
    icon: <ClipboardList size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo'],
  },
  {
    label: 'Despachos',
    path: '/despachos',
    icon: <Truck size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo', 'compras'],
  },
  {
    label: 'Avance de Obra',
    path: '/avance-de-obra',
    icon: <BarChart3 size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo'],
  },
  {
    label: 'Reportes',
    path: '/reportes',
    icon: <FileBarChart size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo'],
  },
  {
    label: 'Maestros',
    path: '/maestros',
    icon: <HardHat size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo'],
  },
  {
    label: 'Contabilidad',
    path: '/contabilidad',
    icon: <Calculator size={18} />,
    roles: ['gerente', 'operaciones', 'administrativo', 'contabilidad'],
  },
];

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const { sidebarOpen, setSidebarOpen } = useUiStore();

  const visibleItems = NAV_ITEMS.filter((item) =>
    user ? item.roles.includes(user.role) : false,
  );

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 z-50 flex h-full w-[280px] flex-col
          bg-[rgba(41,55,66,0.84)] backdrop-blur-md
          transition-transform duration-200
          lg:translate-x-0 lg:z-30
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2.5">
            <img src={logoIcon} alt="Octopaint" className="h-9 w-9 rounded-lg object-cover" />
            <span className="text-xl font-bold text-white tracking-wide">octopaint</span>
          </div>
          <button
            className="rounded-lg p-1 text-white/50 hover:text-white lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {visibleItems.map((item) => (
            <NavLink
              key={item.path + item.label}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive ? 'active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User info at bottom */}
        {user && (
          <div className="border-t border-white/10 px-5 py-4">
            <p className="text-sm font-medium text-white/80 truncate">{user.nombre}</p>
            <p className="text-xs text-white/40 capitalize">{user.role}</p>
          </div>
        )}
      </aside>
    </>
  );
}
